// §S2 — Owner Order Console: a CONTRACT-DRIVEN owner-management primitive for any owner-managed
// collection. Lists real rows over GET /v1/apps/:appId/owner/:collection (ownsApp-gated server-side —
// a non-owner gets an empty list, never a leak) and advances each through its lifecycle via
// PATCH …/:id { status } (validated server-side against the collection's lifecycle) or marks payment
// via PATCH …/:id { data: { paymentStatus } } (validated against ALLOWED_PAYMENT_STATUS).
//
// The owner ACTIONS are DERIVED from the status-lifecycle registry (allowedNext(...'owner')), never a
// hard-coded per-status map — so the console can never drift from the server's transition rules, and it
// lights up orders (orderahead/commerce), rsvps (events), tickets (ticketing) from one component.
// No fabricated rows: the list is exactly the server's items; honest loading/empty/error/unavailable.
import { useCallback, useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { RotateCw, Loader2, CreditCard, X, ClipboardList, Download } from 'lucide-preact';
import { getLifecycle, collectionLifecycleId, allowedNext, getCollectionContract } from '@vibeshell/shared';
import type { StatusLifecycle } from '@vibeshell/shared';
import { t } from '../lib/i18n';

interface OwnerRow {
  id: string;
  status?: string;
  paymentStatus?: string;
  summary?: string;
  name?: string;
  title?: string;
  pickupTime?: string;
  total?: number | string;
  createdAt?: string | number;
  [k: string]: unknown;
}

type Load =
  | { s: 'loading' }
  | { s: 'ready'; items: OwnerRow[] }
  | { s: 'error'; code?: number }
  | { s: 'unavailable' };

// Payment actions mirror ManageBookings — an owner may mark a manually-collected payment paid or
// cancel it. The SET here is small + payment-lifecycle-scoped (not the record lifecycle).
const PAY_ACTIONS: Record<string, { to: string; labelKey: string; danger?: boolean }[]> = {
  awaiting_payment: [{ to: 'marked_paid', labelKey: 'mgmt.act.markPaid' }, { to: 'payment_canceled', labelKey: 'mgmt.act.cancelPayment', danger: true }],
  external_pending: [{ to: 'marked_paid', labelKey: 'mgmt.act.markPaid' }, { to: 'payment_canceled', labelKey: 'mgmt.act.cancelPayment', danger: true }],
  pay_at_business_pending: [{ to: 'marked_paid', labelKey: 'mgmt.act.markPaid' }, { to: 'payment_canceled', labelKey: 'mgmt.act.cancelPayment', danger: true }],
};

/** A state's owner-facing label: an i18n key `mgmt.rec.<lifecycle>.<state>` when present, else the
 *  lifecycle's own ownerLabel (honest English fallback), else the raw id. NEVER a hard-coded status map. */
function stateLabel(lc: StatusLifecycle | null, id: string): string {
  const key = `mgmt.rec.${lc?.id ?? 'x'}.${id}`;
  const s = t(key);
  if (s !== key) return s;
  const st = lc?.states.find((x) => x.id === id);
  return st?.ownerLabel || id;
}

function payLabel(ps?: string): string {
  if (!ps || ps === 'not_required') return '';
  const key = `mgmt.pay.${ps}`;
  const s = t(key);
  return s !== key ? s : ps;
}

/** §S7-CONSOLE-REFRESH — one owner write can create a row in a DIFFERENT collection (approving a
 *  directory submission promotes it into a listing). The console showing that collection re-reads when
 *  this fires. Exported so the writer and the reader name the same thing. */
export const OWNER_RECORDS_CHANGED = 'pomela:owner-records-changed';
export function notifyOwnerRecordsChanged(appId: string, collection: string): void {
  window.dispatchEvent(new CustomEvent(OWNER_RECORDS_CHANGED, { detail: { appId, collection } }));
}

function whenLabel(ts?: string | number): string {
  if (!ts) return '';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

export function ManageRecords({
  appId,
  collection,
  titleKey,
  title,
  icon
}: {
  appId: string;
  collection: string;
  titleKey: string;
  /** §CANONICAL-LANE-DESCRIPTOR — a server-supplied lane label. The descriptor names composed lanes
   *  (e.g. a directory lane's "Submissions") that have no static i18n key, so it wins when present
   *  and the translated titleKey remains the default for every existing console. */
  title?: string;
  icon?: JSX.Element;
}): JSX.Element {
  const lifecycleId = collectionLifecycleId(collection);
  const lifecycle = lifecycleId ? getLifecycle(lifecycleId) : null;
  const statusField = getCollectionContract(collection)?.statusField || 'status';
  // §S7-CONSOLE-COPY — this console serves every owner-managed collection (stock, stock adjustments, directory
  // submissions and listings, tickets…), and every one of them said "No orders yet — new pickup orders show up
  // here". The order wording is kept for orders; everything else says what is true of any record list.
  const ordersConsole = /(^|-)orders$/.test(collection);

  const [load, setLoad] = useState<Load>({ s: 'loading' });
  const [busy, setBusy] = useState<string | null>(null);

  const fetchRows = useCallback(() => {
    setLoad({ s: 'loading' });
    fetch(`/v1/apps/${encodeURIComponent(appId)}/owner/${encodeURIComponent(collection)}`, { credentials: 'include' })
      .then(async (r) => {
        if (r.status === 503) return setLoad({ s: 'unavailable' });
        if (!r.ok) return setLoad({ s: 'error', code: r.status });
        const data = (await r.json()) as { items?: OwnerRow[] };
        setLoad({ s: 'ready', items: Array.isArray(data.items) ? data.items : [] });
      })
      .catch(() => setLoad({ s: 'error' }));
  }, [appId, collection]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // §S7-CONSOLE-REFRESH — approving a directory submission CREATES a row in another collection, and the
  // console showing that collection is a sibling component with its own fetch lifecycle. It had no way to
  // hear about the new listing, so the owner approved a submission and the Listings panel below still
  // showed the old list until they reloaded the page — the approval looked like it had not worked.
  // Each console now re-reads when something it owns is written somewhere else in the workspace.
  useEffect(() => {
    const onChanged = (e: Event) => {
      const d = (e as CustomEvent<{ appId?: string; collection?: string }>).detail;
      if (d?.appId === appId && d?.collection === collection) fetchRows();
    };
    window.addEventListener(OWNER_RECORDS_CHANGED, onChanged);
    return () => window.removeEventListener(OWNER_RECORDS_CHANGED, onChanged);
  }, [appId, collection, fetchRows]);

  const patch = useCallback(
    async (id: string, body: { status?: string } | { data: { paymentStatus: string } }, apply: (row: OwnerRow) => OwnerRow) => {
      setBusy(id);
      try {
        const r = await fetch(`/v1/apps/${encodeURIComponent(appId)}/owner/${encodeURIComponent(collection)}/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body)
        });
        if (r.ok) setLoad((l) => (l.s === 'ready' ? { s: 'ready', items: l.items.map((it) => (it.id === id ? apply(it) : it)) } : l));
      } catch {
        /* leave the row as-is on failure — the server is the authority; a retry re-reads truth */
      } finally {
        setBusy(null);
      }
    },
    [appId, collection]
  );

  // §DEAD-CTRL-FIX — directory listing_submissions: an owner "approve" PROMOTES the submission into a
  // separate canonical listing (atomic/idempotent) and "reject" records a reason — dedicated endpoints,
  // not a plain status PATCH (which would flip status without ever creating the public listing).
  const submissionAct = useCallback(
    async (id: string, status: 'approved' | 'rejected') => {
      setBusy(id);
      try {
        const path = status === 'approved' ? 'approve' : 'reject';
        const r = await fetch(`/v1/apps/${encodeURIComponent(appId)}/owner/listing_submissions/${encodeURIComponent(id)}/${path}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(status === 'rejected' ? { reason: '' } : {}),
        });
        if (r.ok) {
          const j = (await r.json().catch(() => ({}))) as { promotedListingId?: string };
          setLoad((l) => (l.s === 'ready' ? { s: 'ready', items: l.items.map((it) => (it.id === id ? { ...it, [statusField]: status, ...(j.promotedListingId ? { promotedListingId: j.promotedListingId } : {}) } : it)) } : l));
          // the promotion wrote a canonical listing: tell the Listings console, which is a sibling
          if (j.promotedListingId) notifyOwnerRecordsChanged(appId, 'listings');
        }
      } catch {
        /* server is the authority; a retry re-reads truth (approve is idempotent) */
      } finally {
        setBusy(null);
      }
    },
    [appId, statusField]
  );

  const advance = (id: string, status: string) =>
    collection === 'listing_submissions' && (status === 'approved' || status === 'rejected')
      ? submissionAct(id, status)
      : patch(id, { status }, (it) => ({ ...it, [statusField]: status }));
  const setPay = (id: string, paymentStatus: string) => patch(id, { data: { paymentStatus } }, (it) => ({ ...it, paymentStatus }));

  const Ic = icon ?? <ClipboardList size={16} />;

  return (
    /* §MGMT-READINESS — each owner console owns an INDEPENDENT fetch lifecycle, but the panel's only
       readiness signal (AppHubV2's data-statehost="manage") is emitted once for the whole surface, so
       nothing outside can tell when a SECTION has actually settled. Measured consequence: an external
       observer enumerating this surface sees a different control set run to run (37 vs 38 controls on
       bloom-gprh across identical runs) because the availability/records sections resolve after the
       panel says "loaded". Publishing each section's own state costs nothing, changes no behaviour,
       and makes the surface honestly observable — to a test, and to anything else that needs to know
       the data has arrived. */
    <section aria-label={title || t(titleKey)} data-records-state={load.s} data-records-collection={collection} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="pf-row">
        <span className="pf-mgmt-panel__t" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {Ic} {title || t(titleKey)}
          {load.s === 'ready' && <span className="pf-badge" style={{ marginInlineStart: 6 }}>{load.items.length}</span>}
        </span>
        <button type="button" className="pf-btn pf-btn--sm" onClick={fetchRows} disabled={load.s === 'loading'}>
          <RotateCw size={13} /> {t('mgmt.refresh')}
        </button>
      </div>

      {load.s === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[0, 1].map((i) => <div key={i} className="skeleton-shimmer" style={{ height: 56, borderRadius: 'var(--r-lg)', border: '1px solid var(--border)' }} />)}
        </div>
      )}

      {load.s === 'unavailable' && (
        <div className="pf-mgmt-panel"><span className="pf-mgmt-panel__body"><span className="pf-mgmt-panel__t">{t('mgmt.rec.unavailableT')}</span><span className="pf-mgmt-panel__s">{t(ordersConsole ? 'mgmt.rec.unavailableS' : 'mgmt.rec.unavailableRecordsS')}</span></span></div>
      )}

      {load.s === 'error' && (
        <div className="pf-mgmt-panel">
          <span className="pf-mgmt-panel__body"><span className="pf-mgmt-panel__t">{t('mgmt.rec.errT')}</span><span className="pf-mgmt-panel__s">{load.code === 401 ? t(ordersConsole ? 'mgmt.rec.errAuth' : 'mgmt.rec.errAuthRecords') : t('mgmt.rec.errGeneric')}</span></span>
          <button type="button" className="pf-btn pf-btn--sm" onClick={fetchRows}><RotateCw size={13} /> {t('common.retry')}</button>
        </div>
      )}

      {load.s === 'ready' && load.items.length === 0 && (
        <div className="pf-empty" style={{ marginTop: 2 }}>
          <span className="pf-bubble pf-mark-halo" aria-hidden="true"><ClipboardList size={22} /></span>
          <p className="pf-empty__title">{t(ordersConsole ? 'mgmt.rec.emptyT' : 'mgmt.rec.emptyRecordsT')}</p>
          <p className="pf-empty__lede">{t(ordersConsole ? 'mgmt.rec.emptyS' : 'mgmt.rec.emptyRecordsS')}</p>
        </div>
      )}

      {/* §INT-F2 — real CSV export. The server route (GET /owner/:collection/export.csv,
          ownsApp-gated, RFC-4180 + formula-injection-safe) already existed but had no
          reachable UI. Same-origin relative URL → the creator session cookie rides and the
          server's Content-Disposition triggers the download. */}
      {load.s === 'ready' && load.items.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <a className="pf-btn pf-btn--sm" href={`/v1/apps/${encodeURIComponent(appId)}/owner/${encodeURIComponent(collection)}/export.csv`} download>
            <Download size={13} /> {t('mgmt.rec.exportCsv')}
          </a>
        </div>
      )}
      {load.s === 'ready' && load.items.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {load.items.map((row) => {
            const status = (row[statusField] as string) ?? row.status ?? lifecycle?.initial ?? '';
            // Owner action set is DERIVED from the lifecycle — never a hard-coded per-status map.
            const nextStates = lifecycle ? allowedNext(lifecycle, status, 'owner') : [];
            const payActs = PAY_ACTIONS[row.paymentStatus ?? ''] ?? [];
            const title = row.summary || row.name || row.title || `#${row.id.slice(0, 6)}`;
            const subParts = [
              row.pickupTime ? String(row.pickupTime) : '',
              typeof row.total !== 'undefined' && row.total !== null ? `$${row.total}` : '',
              whenLabel(row.createdAt)
            ].filter(Boolean);
            const pay = payLabel(row.paymentStatus);
            return (
              <li key={row.id} className="pf-mgmt-panel" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                <span className="pf-mgmt-panel__body">
                  <span className="pf-mgmt-panel__t" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {title}
                    <span className="pf-badge">{stateLabel(lifecycle, status)}</span>
                    {pay && <span className="pf-badge pf-badge--seed">{pay}</span>}
                  </span>
                  <span className="pf-mgmt-panel__s">{subParts.join(' · ') || '—'}</span>
                </span>
                {(nextStates.length > 0 || payActs.length > 0) && (
                  <div className="pf-row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                    {busy === row.id && <Loader2 size={13} className="pf-spin" />}
                    {nextStates.map((to) => (
                      <button key={to} type="button" className="pf-btn pf-btn--sm" disabled={busy === row.id} onClick={() => void advance(row.id, to)}>
                        {stateLabel(lifecycle, to)}
                      </button>
                    ))}
                    {payActs.map((a) => (
                      <button key={a.to} type="button" className="pf-btn pf-btn--sm" disabled={busy === row.id} onClick={() => void setPay(row.id, a.to)}>
                        {a.to === 'marked_paid' ? <CreditCard size={12} /> : <X size={12} />} {t(a.labelKey)}
                      </button>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
